import { useTranslation } from "react-i18next";
import { Cloud, Wrench } from "lucide-react";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { tp } from "@/lib/plural";
import { formatEffort } from "@/lib/wricef-status";
import type { WricefCatalogSummary } from "@/types/wricef";

import {
  CLEAN_CORE_LEVEL_ORDER,
  cloudReadyPct,
  conversionSplit,
  type ExtensibilityCounts,
  EXTENSIBILITY_ORDER,
  extensibilitySplit,
  type ModuleDensity,
  moduleDensity,
  type PostureCounts,
  postureFromLevels,
  assessmentCoverage,
  effortSplit,
  remediationBacklog,
} from "./cleanCoreData";
import {
  CONVERSION_COLORS,
  CONVERSION_LABELS,
  EXTENSIBILITY_META,
  LEVEL_BAR,
  LEVEL_LABEL,
} from "./cleanCoreMeta";

const POSTURE_KEYS = CLEAN_CORE_LEVEL_ORDER;

/** Barra empilhada A/B/C/D a partir de contagens absolutas. */
function PostureBar({
  counts,
  height = "h-5",
}: {
  counts: PostureCounts;
  height?: string;
}) {
  const { t } = useTranslation();
  const total = POSTURE_KEYS.reduce((acc, k) => acc + counts[k], 0) + counts.unknown;
  return (
    <div
      className={`flex w-full overflow-hidden rounded ${height} bg-muted`}
      role="img"
      aria-label={[
        ...POSTURE_KEYS.map((k) => t("{{n}} level {{level}}", { n: counts[k], level: k })),
        t("{{n}} not assessed", { n: counts.unknown }),
      ].join(", ")}
    >
      {POSTURE_KEYS.map((k) => {
        const n = counts[k];
        if (n <= 0) return null;
        return (
          <div
            key={k}
            data-level={k}
            className={LEVEL_BAR[k]}
            style={{ flexGrow: n, flexBasis: 0 }}
            title={`${n} · ${LEVEL_LABEL[k]}`}
          />
        );
      })}
      {/* O sem nota vira segmento cinza, como a legenda diz. Sem ele, 68 A e 173
          sem nota desenhavam uma barra 100% verde. */}
      {counts.unknown > 0 && (
        <div
          data-level="unknown"
          className="bg-muted-foreground/40"
          style={{ flexGrow: counts.unknown, flexBasis: 0 }}
          title={`${counts.unknown} · ${t("no grade")}`}
        />
      )}
      {total === 0 && <div className="flex-1" />}
    </div>
  );
}

function ModuleRow({ b }: { b: ModuleDensity }) {
  const { t } = useTranslation();
  const remPct = b.total > 0 ? Math.round((b.remediation / b.total) * 100) : 0;
  const hot = remPct >= 40 && b.remediation > 0;
  return (
    <div className="grid grid-cols-[10rem_1fr_5rem] items-center gap-3">
      <div className="text-right">
        <div className="truncate text-xs font-semibold">{b.module}</div>
        <div className="font-mono text-[10px] text-muted-foreground">
          {tp(t, b.total, "{{n}} object", "{{n}} objects")}
        </div>
      </div>
      <div className={hot ? "rounded ring-2 ring-red-300/60" : ""}>
        <PostureBar counts={b.counts} />
      </div>
      <div className="text-left font-mono text-xs font-bold text-red-600">
        {b.remediation || <span className="text-muted-foreground">0</span>}
        {hot && (
          <span className="ml-1 rounded bg-red-100 px-1 py-px text-[9px] font-bold text-red-700">
            {remPct}%
          </span>
        )}
      </div>
    </div>
  );
}

function ExtensibilityRows({
  counts,
  total,
}: {
  counts: ExtensibilityCounts;
  total: number;
}) {
  return (
    <div className="flex flex-col gap-3.5">
      {EXTENSIBILITY_ORDER.map((t) => {
        const n = counts[t];
        const meta = EXTENSIBILITY_META[t];
        const pct = total > 0 ? Math.round((n / total) * 100) : 0;
        return (
          <div key={t} className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between text-xs">
              <span className="font-semibold">{meta.label}</span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {n} · {pct}%
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div
                className={`h-full ${meta.bar}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-[11px] leading-snug text-muted-foreground">
              {meta.hint}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * O cockpit Clean Core, lido do resumo do catálogo inteiro.
 *
 * Recebia as 1.000 primeiras linhas em ordem alfabética e agregava no navegador.
 * Medido em 24/09/2026 no `Janela 144 meses [21/09]`: "27 of 1.000" cloud-ready e
 * 737 em C/D, contra 620 de 7.288 e 5.574 no catálogo (#922).
 */
export function OverviewView({
  summary,
  version,
  scanInProgress = false,
}: {
  summary: WricefCatalogSummary;
  version?: string;
  /** #1111: há varredura rodando sobre este catálogo (visão acumulada). */
  scanInProgress?: boolean;
}) {
  const { t } = useTranslation();
  const posture = useMemo(
    () => postureFromLevels(summary.by_clean_core_level),
    [summary.by_clean_core_level],
  );
  const cloudPct = cloudReadyPct(posture);
  const coverage = assessmentCoverage(posture);
  const backlog = remediationBacklog(posture);
  const effort = useMemo(
    () => effortSplit(summary.effort_h_by_clean_core_level),
    [summary.effort_h_by_clean_core_level],
  );
  const { hot, rest } = useMemo(
    () => moduleDensity(summary.by_stream_clean_core_level, summary.stream_labels),
    [summary.by_stream_clean_core_level, summary.stream_labels],
  );
  const extCounts = useMemo(
    () => extensibilitySplit(summary.by_extensibility_target),
    [summary.by_extensibility_target],
  );
  const conversions = useMemo(
    () => conversionSplit(summary.by_conversion_type),
    [summary.by_conversion_type],
  );
  const effortH = effort.total;

  const total = coverage.total;
  const cloudReadyN = posture.A;
  const effortLabel = formatEffort(effortH);
  const extTotal =
    extCounts.key_user + extCounts.side_by_side + extCounts.developer;
  const maxConversion = conversions.reduce((m, c) => Math.max(m, c.count), 0);

  // Soma dos módulos colapsados, pra linha "Outras N módulos".
  const restCounts: PostureCounts = rest.reduce(
    (acc, b) => {
      acc.A += b.counts.A;
      acc.B += b.counts.B;
      acc.C += b.counts.C;
      acc.D += b.counts.D;
      acc.unknown += b.counts.unknown;
      return acc;
    },
    { A: 0, B: 0, C: 0, D: 0, unknown: 0 } as PostureCounts,
  );
  const restTotal = rest.reduce((acc, b) => acc + b.total, 0);
  const restRemediation = rest.reduce((acc, b) => acc + b.remediation, 0);

  return (
    <div className="flex flex-col gap-6">
      {/* linha de confiança / trust */}
      {/* #1111: com a varredura rodando, os números abaixo são o catálogo
          acumulado, que ainda mistura linhas da execução anterior. O painel da
          varredura recarrega esta aba a cada lote; o aviso diz o que se lê. */}
      {scanInProgress && (
        <div
          data-testid="scan-in-progress-note"
          className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800"
        >
          {t(
            "Scan in progress: these numbers update with each batch and only settle when it finishes.",
          )}
        </div>
      )}
      <div className="font-mono text-[11px] text-muted-foreground">
        {t("ABAP base probed ·")}{" "}
        <span className="font-semibold text-foreground">
          {total.toLocaleString("pt-BR")}
        </span>{" "}
        {t("custom objects · 3-tier cascade")}
        {version ? ` · ${t("rules v{{version}}", { version })}` : ""}{" "}
        {t("· deterministic + LLM")}
      </div>

      {/* headline cloud-ready % + barra empilhada de postura */}
      <div className="flex flex-col gap-3">
        <div className="flex items-baseline gap-3">
          <span className="text-4xl font-extrabold tracking-tight">
            {cloudPct}%
          </span>
          <span className="text-sm text-muted-foreground">
            {t(
              "Cloud-ready (level A) — {{n}} of {{total}} are already in clean core, direct adoption, no remediation.",
              {
                n: cloudReadyN.toLocaleString("pt-BR"),
                total: total.toLocaleString("pt-BR"),
              },
            )}
          </span>
        </div>
        <PostureBar counts={posture} height="h-7" />
        <div className="flex flex-wrap gap-4 font-mono text-[10px] text-muted-foreground">
          {POSTURE_KEYS.map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className={`inline-block h-2.5 w-2.5 rounded-sm ${LEVEL_BAR[k]}`} />
              {LEVEL_LABEL[k]} · {posture[k]}
            </span>
          ))}
          {posture.unknown > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-muted-foreground/40" />
              {t("No grade")} · {posture.unknown}
            </span>
          )}
        </div>
      </div>

      {/* 3 callouts: cloud-ready % · remediação C+D · effort total */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border bg-white p-4">
          <div className="flex items-center gap-1.5 text-3xl font-extrabold text-emerald-600">
            <Cloud className="h-5 w-5" />
            {cloudPct}%
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {t("Cloud-ready (A)")}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {tp(
              t,
              cloudReadyN,
              "{{n}} object already in clean core: direct adoption, no remediation.",
              "{{n}} objects already in clean core: direct adoption, no remediation.",
            )}
            {/* Sem esta linha, "28%" lê como "os outros 72% não são cloud-ready",
                quando na verdade não foram avaliados. */}
            {coverage.unassessed > 0 && (
              <>
                {" "}
                {t("Of the base, {{pct}}% was assessed ({{assessed}} of {{total}}).", {
                  pct: coverage.pct,
                  assessed: coverage.assessed.toLocaleString("pt-BR"),
                  total: coverage.total.toLocaleString("pt-BR"),
                })}
              </>
            )}
          </div>
        </div>
        {/* Enquanto houver objeto sem nota, o zero é "nada CONHECIDO", não "nada".
            ✅ Medido em 20/08: 0 em C+D com 173 dos 241 nunca classificados — a tela
            anunciava backlog vazio sobre 72% de base não avaliada. */}
        <div
          className={
            backlog.settled
              ? "rounded-lg border border-red-300 bg-red-50 p-4"
              : "rounded-lg border border-amber-300 bg-amber-50 p-4"
          }
        >
          <div
            className={
              backlog.settled
                ? "flex items-center gap-1.5 text-3xl font-extrabold text-red-600"
                : "flex items-center gap-1.5 text-3xl font-extrabold text-amber-600"
            }
          >
            <Wrench className="h-5 w-5" />
            {backlog.settled ? (
              backlog.confirmed
            ) : (
              <span>
                {backlog.confirmed}
                <span className="text-xl font-bold">–{backlog.upperBound}</span>
              </span>
            )}
          </div>
          <div
            className={
              backlog.settled
                ? "font-mono text-[10px] uppercase tracking-wide text-red-700"
                : "font-mono text-[10px] uppercase tracking-wide text-amber-700"
            }
          >
            {t("Remediation (C + D)")}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {backlog.settled ? (
              t("The real backlog: they need an extension decision before migrating.")
            ) : (
              t(
                "{{confirmed}} confirmed, and {{unassessed}} objects were never assessed: the backlog can still grow.",
                {
                  confirmed: backlog.confirmed.toLocaleString("pt-BR"),
                  unassessed: backlog.unassessed.toLocaleString("pt-BR"),
                },
              )
            )}
          </div>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <div className="text-3xl font-extrabold tracking-tight">
            {effortLabel ? `${effortH}h` : "—"}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            {t("Estimated effort")}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {/* #1111: o Gate A soma só o escopo de desenvolvimento; este card
                soma o catálogo inteiro. Cada número diz de onde vem. */}
            {effortLabel
              ? t(
                  "{{effort}} across the whole catalog ({{n}} objects), calibrated by WRICEF × complexity.",
                  {
                    effort: effortLabel,
                    n: (summary.total_rows ?? total).toLocaleString("pt-BR"),
                  },
                )
              : t("No conversion estimate in the catalog yet.")}
            {/* Sem esta linha o total passa por estimativa calibrada quando a maior
                parte do peso vem de objeto que nunca recebeu nota. ✅ 19.556h no total,
                5.582h nos avaliados. */}
            {effort.unassessed > 0 && (
              <>
                {" "}
                {t(
                  "Only {{assessed}}h come from assessed objects; {{unassessed}}h sit on objects with no clean core grade.",
                  { assessed: effort.assessed, unassessed: effort.unassessed },
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* densidade por módulo, ordenado por remediação */}
      <div className="flex flex-col gap-2">
        <div className="text-sm font-semibold">
          {t("Risk is concentrated by module")}
        </div>
        <p className="text-xs text-muted-foreground">
          {backlog.confirmed > 0
            ? t("Each bar is the module's A/B/C/D composition, ordered by remediation volume (C+D). That is where the analyst prioritizes the gap analysis.")
            : t("No module has remediation yet, so the bars are ordered by object volume. Grey means not assessed.")}
        </p>
        <div className="mt-3 flex flex-col gap-2.5">
          {hot.length === 0 && rest.length === 0 ? (
            <p className="text-xs italic text-muted-foreground">
              {t("No modules in the catalog yet.")}
            </p>
          ) : (
            hot.map((b) => <ModuleRow key={b.module} b={b} />)
          )}
          {rest.length > 0 && (
            <div className="grid grid-cols-[10rem_1fr_5rem] items-center gap-3 opacity-70">
              <div className="text-right text-xs font-semibold">
                {tp(t, rest.length, "{{n}} other module", "{{n}} other modules")}
                <div className="font-mono text-[10px] font-normal text-muted-foreground">
                  {tp(t, restTotal, "{{n}} object", "{{n}} objects")}
                </div>
              </div>
              <PostureBar counts={restCounts} />
              <div className="text-left font-mono text-xs font-bold text-muted-foreground">
                {restRemediation}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* split de extensão + split de conversão */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold">
            {t("How to extend in S/4")}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("The level says how clean. The extensibility model says how to extend — in-app vs BTP vs ABAP Cloud.")}
          </p>
          <div className="mt-2">
            {extTotal === 0 ? (
              <p className="text-xs italic text-muted-foreground">
                {t("No object has an extension model assigned yet.")}
              </p>
            ) : (
              <ExtensibilityRows counts={extCounts} total={extTotal} />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-sm font-semibold">
            {t("What to do with each object")}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("Conversion split of the catalog. Each object carries the type + the target in S/4.")}
          </p>
          <div className="mt-2 flex flex-col gap-2.5">
            {conversions.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">
                {t("No conversion type classified yet.")}
              </p>
            ) : (
              conversions.map((c) => {
                const pct =
                  maxConversion > 0
                    ? Math.round((c.count / maxConversion) * 100)
                    : 0;
                return (
                  <div key={c.tipo} className="flex flex-col gap-1">
                    <div className="flex items-baseline justify-between text-xs">
                      <Badge
                        variant="outline"
                        className={`text-[11px] ${
                          CONVERSION_COLORS[c.tipo] ??
                          "bg-gray-100 text-gray-800 border-gray-200"
                        }`}
                      >
                        {CONVERSION_LABELS[c.tipo] ?? c.tipo}
                      </Badge>
                      <span className="font-mono text-[11px] font-bold text-muted-foreground">
                        {c.count}
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
